/**
 * The words of the search terms card (search-terms-card.ts) and of what its
 * Track these did (bridges/keywords.ts): the reason beside each proposed
 * keyword, and the lines the done card and the Activity entry read.
 *
 * QA note 1a0e77b9, Manu, 25 Sep 26. Free of imports on purpose: the home
 * page reads these through agentTypes.ts, and the worker through the card
 * and the bridge, so the owner reads one set of words everywhere.
 */

/**
 * Google reports a search seen fewer than 15 times as "fewer than 15", and
 * fetch-search-insights stores that as 15. A 15 is therefore an upper bound,
 * and the reason says so rather than claiming 15 searches.
 */
export const SEARCH_FLOOR = 15;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "last month" for the month before `now`, else "in Aug 26". */
export function monthWords(month: string, now: Date): string {
  const m = /^(\d{4})-(\d{2})$/.exec(String(month ?? ''));
  if (!m) return 'last month';
  const year = Number(m[1]);
  const index = Number(m[2]) - 1;
  const last = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  if (last.getUTCFullYear() === year && last.getUTCMonth() === index) return 'last month';
  if (now.getUTCFullYear() === year && now.getUTCMonth() === index) return 'this month';
  return `in ${MONTHS[index] ?? m[2]} ${String(year).slice(2)}`;
}

/**
 * "Searched 54 times last month". Several searches that land on one keyword
 * are added up; a count at Google's floor makes the sum an upper bound ("up
 * to"), or, beside exact counts, the exact part a lower bound ("more than").
 */
export function searchesReason(values: number[], month: string, now: Date): string {
  const when = monthWords(month, now);
  const sum = values.reduce((s, v) => s + v, 0);
  const exact = values.filter((v) => v !== SEARCH_FLOOR);
  if (exact.length === values.length) return `Searched ${sum} times ${when}`;
  if (!exact.length) return `Searched up to ${sum} times ${when}`;
  return `Searched more than ${exact.reduce((s, v) => s + v, 0)} times ${when}`;
}

/** What the owner is told the agent does next, once a keyword is tracked. */
export function firstReadingLine(count: number): string {
  return count === 1
    ? 'The agent starts reading where you rank for it at the next weekly check.'
    : 'The agent starts reading where you rank for these at the next weekly check.';
}

export interface KeywordsTrackResult {
  tracked?: string[];
  replaced?: Array<{ from: string; to: string }>;
  already?: string[];
  not_tracked?: Array<{ keyword: string; why: string }>;
}

const quoted = (list: string[]) => list.map((k) => `"${k}"`).join(', ');

/** The done card's lines for a keywords_track result. */
export function keywordsTrackLines(result: unknown): string[] {
  const r = (result ?? {}) as KeywordsTrackResult;
  const tracked = Array.isArray(r.tracked) ? r.tracked.map(String) : [];
  const replaced = Array.isArray(r.replaced) ? r.replaced.filter((x) => x && x.from && x.to) : [];
  const already = Array.isArray(r.already) ? r.already.map(String) : [];
  const missed = Array.isArray(r.not_tracked) ? r.not_tracked.filter((x) => x && x.keyword) : [];
  const lines: string[] = [];
  if (tracked.length) lines.push(`Now tracking ${quoted(tracked)}.`);
  for (const x of replaced) lines.push(`Now tracking "${x.to}" in place of "${x.from}".`);
  if (already.length) lines.push(`Tracked already: ${quoted(already)}.`);
  for (const x of missed) lines.push(`Not tracked: "${x.keyword}", ${x.why}.`);
  const added = tracked.length + replaced.length;
  if (added) lines.push(firstReadingLine(added));
  if (!lines.length) lines.push('Nothing changed in your tracked keywords.');
  return lines;
}
